import React from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import MobileBottomBar from "../components/MobileBottomBar";
import { ShoppingBag, Minus, Plus, Trash2 } from "lucide-react";
import { getImageUrl } from "../utils/getImageUrl";

export default function Cart() {
  const navigate = useNavigate();
  const { cart, increaseQty, decreaseQty, removeFromCart, updateNote } = useCart();

  const total = cart.reduce((sum, item) => {
    const finalPrice =
      item.offerPrice && item.offerPrice > 0 && item.offerPrice < item.price
        ? item.offerPrice
        : item.price;

    return sum + finalPrice * item.qty;
  }, 0);

  const totalWithoutDiscount = cart.reduce(
    (sum, item) => sum + item.price * item.qty,
    0,
  );

  const totalDiscount = totalWithoutDiscount - total;

  const totalPrice = total + totalDiscount;

  return (
    <div className="min-h-screen bg-black pt-6 max-w-6xl mx-auto px-6 pb-20">
      <button
        onClick={() => navigate(-1)}
        className="mb-6 flex items-center gap-2 text-supernova-500 hover:text-supernova-400
                   transition font-semibold"
      >
        <span className="text-xl">←</span> Volver
      </button>
      <h1 className="text-center text-3xl font-bold mb-6 text-white">Tu Carrito</h1>

      {cart.length === 0 ? (
        <div className="text-center mt-16">
          <ShoppingBag size={60} className="mx-auto text-gray-600" />
          <p className="text-gray-400 mt-4 text-lg">Tu carrito está vacío</p>
        </div>
      ) : (
        <div className="max-w-xl mx-auto px-4">
          {cart.map((item) => {
            const itemId = item.id ?? item._id;
            return (
            <div
              key={itemId}
              className="bg-zinc-900 rounded-xl p-4 mb-4 border border-zinc-800"
            >
              <div className="flex gap-4">
              <div className="w-24 h-24 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
                <img
                  src={getImageUrl(item.images?.[0])}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex-1 flex flex-col justify-between">
                <h2 className="font-semibold text-white">{item.title}</h2>
                {item.offerPrice ? (
                  <>
                    <p className="text-gray-400 line-through text-lg">
                      ${item.price.toLocaleString("es-CO")}
                    </p>
                    <p className="text-supernova-500 font-bold">
                      ${item.offerPrice.toLocaleString("es-CO")}
                    </p>
                  </>
                ) : (
                  <p className="text-supernova-500 font-bold">
                    ${item.price.toLocaleString("es-CO")}
                  </p>
                )}

                <div className="flex items-center gap-3 mt-2">
                  <button
                    onClick={() => decreaseQty(itemId)}
                    className="bg-zinc-700 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-zinc-600"
                  >
                    <Minus size={16} />
                  </button>

                  <span className="text-lg font-semibold text-white">{item.qty}</span>

                  <button
                    onClick={() => increaseQty(itemId)}
                    className="bg-supernova-500 text-white w-8 h-8 rounded-full flex items-center justify-center"
                  >
                    <Plus size={16} />
                  </button>

                  <button
                    onClick={() => removeFromCart(itemId)}
                    className="ml-auto text-red-500 hover:text-red-400"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
              </div>
              <textarea
                value={item.note || ""}
                onChange={(e) => updateNote(itemId, e.target.value)}
                placeholder="Personalización: sin cebolla, extra queso..."
                rows={3}
                className="mt-3 w-full text-sm bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-supernova-400 text-white placeholder-zinc-500"
              />
            </div>
            );
          })}

          <div className="bg-zinc-900 rounded-xl p-5 mt-6 border border-zinc-800">
            <h2 className="text-xl font-bold mb-3 text-white">Resumen</h2>

            <div className="flex justify-between">
              <span className="text-gray-400">Subtotal</span>
              <span className="font-bold text-white">
                ${totalPrice.toLocaleString("es-CO")}
              </span>
            </div>
            {totalDiscount > 0 && (
              <div className="flex justify-between text-green-400 font-semibold">
                <span>Ahorro</span>
                <span>- ${totalDiscount.toLocaleString("es-CO")}</span>
              </div>
            )}

            <hr className="my-3 border-zinc-700" />

            <div className="flex justify-between text-lg font-bold">
              <span className="text-white">Total</span>
              <span className="text-supernova-500">
                ${total.toLocaleString("es-CO")}
              </span>
            </div>

            <button
              onClick={() => navigate("/checkout")}
              className="w-full mt-6 bg-supernova-500 text-white py-3 rounded-xl font-bold hover:bg-supernova-600"
              data-testid="checkout-button"
            >
              Continuar con la compra
            </button>
          </div>
        </div>
      )}

      <MobileBottomBar cartCount={cart.length} />
    </div>
  );
}
